import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Settings, Mail, Bell, Save } from 'lucide-react';
import { PageLayout } from '../components/layout/PageLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Button } from '../components/ui/button';
import { api } from '../lib/api';
import { toast } from 'sonner';

const NotificationPreferencesPage = () => {
  const navigate = useNavigate();
  const [prefs, setPrefs] = useState(null);
  const [labels, setLabels] = useState({});
  const [types, setTypes] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get('/notifications/preferences');
        setPrefs({ in_app: res.data.in_app, email: res.data.email });
        setLabels(res.data.labels || {});
        setTypes(res.data.types || []);
      } catch (e) {
        toast.error('Failed to load preferences');
      }
    };
    load();
  }, []);

  const toggle = (channel, type) => {
    setPrefs((prev) => ({
      ...prev,
      [channel]: { ...prev[channel], [type]: !prev[channel][type] },
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.patch('/notifications/preferences', prefs);
      toast.success('Preferences saved');
    } catch (e) {
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  if (!prefs) {
    return (
      <PageLayout>
        <div className="py-20 flex justify-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <button
          onClick={() => navigate('/notifications')}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
          data-testid="back-to-notifications-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Back to notifications
        </button>

        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight flex items-center gap-3">
            <Settings className="w-8 h-8 text-primary" />
            Notification Preferences
          </h1>
          <p className="text-muted-foreground mt-2 text-sm">
            Choose which notifications you want to receive in-app and by email.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Notification Channels</CardTitle>
            <CardDescription>
              Toggle in-app (bell icon) and email delivery per event type.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="hidden sm:grid grid-cols-[1fr_auto_auto] gap-4 pb-3 border-b border-border/50 mb-3">
              <div />
              <div className="flex items-center gap-1 text-xs text-muted-foreground font-medium w-16 justify-center">
                <Bell className="w-3.5 h-3.5" /> In-app
              </div>
              <div className="flex items-center gap-1 text-xs text-muted-foreground font-medium w-16 justify-center">
                <Mail className="w-3.5 h-3.5" /> Email
              </div>
            </div>

            <ul className="divide-y divide-border/40">
              {types.map((t) => (
                <li
                  key={t}
                  className="grid grid-cols-[1fr_auto_auto] gap-4 sm:gap-6 items-center py-4"
                  data-testid={`pref-row-${t}`}
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{labels[t] || t}</p>
                  </div>
                  <div className="flex items-center justify-center w-16">
                    <Switch
                      checked={!!prefs.in_app[t]}
                      onCheckedChange={() => toggle('in_app', t)}
                      data-testid={`pref-${t}-in-app`}
                    />
                  </div>
                  <div className="flex items-center justify-center w-16">
                    <Switch
                      checked={!!prefs.email[t]}
                      onCheckedChange={() => toggle('email', t)}
                      data-testid={`pref-${t}-email`}
                    />
                  </div>
                </li>
              ))}
            </ul>

            <div className="flex justify-end pt-6">
              <Button onClick={handleSave} disabled={saving} className="gap-2" data-testid="save-preferences-btn">
                <Save className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save Preferences'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
};

export default NotificationPreferencesPage;
